import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  FormControlLabel,
  Switch,
  Autocomplete,
  InputAdornment,
  Checkbox,
  FormGroup,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

import {
  getAllDiscounts,
  createDiscount,
  updateDiscount,
  deleteDiscount,
  getDiscountsByType,
  AllDiscountTypes,
  PromoDiscount,
  UserDiscount,
  applyDiscount
} from '../../firebase/services/discountService';
import { getVesselsByType } from '../../firebase/services/vesselService';
import { doc, getDoc, collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../firebase/config';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`discount-tabpanel-${index}`}
      aria-labelledby={`discount-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DiscountsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [discounts, setDiscounts] = useState<AllDiscountTypes[]>([]);
  const [filteredDiscounts, setFilteredDiscounts] = useState<AllDiscountTypes[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<AllDiscountTypes | null>(null);
  const [isNewDiscount, setIsNewDiscount] = useState(false);
  const [isCopyDiscount, setIsCopyDiscount] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form data for different discount types
  const [formData, setFormData] = useState<any>({});
  const [vesselTypes, setVesselTypes] = useState<string[]>([]);
  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    loadDiscounts();
    loadVesselTypes();
  }, []);

  useEffect(() => {
    filterDiscounts();
  }, [discounts, searchTerm, statusFilter, tabValue]);

  const loadDiscounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const allDiscounts = await getAllDiscounts();
      setDiscounts(allDiscounts);
    } catch (err: any) {
      console.error('Error loading discounts:', err);
      setError(err.message || 'Failed to load discounts');
    } finally {
      setLoading(false);
    }
  };

  const loadVesselTypes = async () => {
    try {
      // Get vessel types directly from actual vessels in the system
      const allVessels = await getVesselsByType();
      const types = Object.keys(allVessels).filter(type => allVessels[type].length > 0);
      
      console.log('Loaded vessel types from vessels:', types);
      console.log('All vessels by type:', allVessels);
      
      setVesselTypes(types);
    } catch (err) {
      console.error('Error loading vessel types:', err);
      setError('Failed to load vessel types');
      setVesselTypes([]);
    }
  };

  const filterDiscounts = () => {
    let filtered = discounts;

    // Filter by tab (discount type)
    if (tabValue === 1) filtered = filtered.filter(d => d.discountType === 'promo');
    else if (tabValue === 2) filtered = filtered.filter(d => d.discountType === 'user');

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(discount => {
        const name = (discount as any).name || (discount as any).code || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filter by status
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(discount => discount.isActive === isActive);
    }

    setFilteredDiscounts(filtered);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddDiscount = () => {
    const discountType = tabValue === 1 ? 'promo' : 'user';
    
    const baseForm = {
      discountType,
      type: 'percentage',
      amount: 0,
      isActive: true,
      startDate: new Date(),
      endDate: null,
      applicableVesselTypes: [],
      // Duration-based fields
      freeDurationMinutes: 0,
      additionalHourPricing: 'regular',
      additionalHourDiscount: 0,
      additionalHourRate: 0,
      // Usage tracking fields
      usageLimit: null,
      usageCount: 0
    };

    if (discountType === 'promo') {
      setFormData({
        ...baseForm,
        code: '',
        name: '',
        usageLimit: null,
        usageCount: 0,
        minPurchaseAmount: null,
        userRestriction: 'none',
        // Time-based restrictions (optional)
        daysOfWeek: [],
        hoursOfDay: []
      });
    } else if (discountType === 'user') {
      setFormData({
        ...baseForm,
        name: '',
        userType: 'all',
        minBookings: null,
        maxUses: null,
        usageLimit: null,
        usageCount: 0
      });
    }

    setEditingDiscount(null);
    setIsNewDiscount(true);
    setIsCopyDiscount(false);
    setEditDialogOpen(true);
  };

  const handleEditDiscount = (discount: AllDiscountTypes) => {
    setEditingDiscount(discount);
    setFormData({ ...discount });
    setIsNewDiscount(false);
    setIsCopyDiscount(false);
    setEditDialogOpen(true);
  };

  const handleCopyDiscount = (discount: AllDiscountTypes) => {
    // Create a copy with smart defaults based on discount type
    let copyData: any;

    if (discount.discountType === 'promo') {
      const promoDiscount = discount as PromoDiscount;
      copyData = {
        ...promoDiscount,
        id: undefined, // Remove ID so it creates a new one
        usageCount: 0, // Reset usage count
        isActive: true, // Set as active by default
        code: `${promoDiscount.code}_COPY`,
        name: promoDiscount.name ? `${promoDiscount.name} (Copy)` : undefined,
      };
    } else if (discount.discountType === 'user') {
      const userDiscount = discount as UserDiscount;
      copyData = {
        ...userDiscount,
        id: undefined, // Remove ID so it creates a new one
        usageCount: 0, // Reset usage count
        isActive: true, // Set as active by default
        name: `${userDiscount.name} (Copy)`,
      };
    }

    // Set as new discount and open dialog
    setEditingDiscount(null);
    setFormData(copyData);
    setIsNewDiscount(true);
    setIsCopyDiscount(true);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (discount: AllDiscountTypes) => {
    setEditingDiscount(discount);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editingDiscount) return;

    try {
      await deleteDiscount(editingDiscount.id);
      setDiscounts(prev => prev.filter(d => d.id !== editingDiscount.id));
      setDeleteDialogOpen(false);
      setEditingDiscount(null);
      setSuccess('Discount deleted successfully!');
    } catch (err: any) {
      console.error('Error deleting discount:', err);
      setError(err.message || 'Failed to delete discount');
    }
  };

  const handleSaveDiscount = async () => {
    try {
      setSaving(true);
      setError(null);

      if (isNewDiscount) {
        const id = await createDiscount(formData);
        const newDiscount = { ...formData, id };
        setDiscounts(prev => [...prev, newDiscount]);
        setSuccess('Discount created successfully!');
      } else if (editingDiscount) {
        await updateDiscount(editingDiscount.id, formData);
        setDiscounts(prev => 
          prev.map(d => d.id === editingDiscount.id ? { ...formData, id: editingDiscount.id } : d)
        );
        setSuccess('Discount updated successfully!');
      }

      setEditDialogOpen(false);
      setEditingDiscount(null);
      setFormData({});
    } catch (err: any) {
      console.error('Error saving discount:', err);
      setError(err.message || 'Failed to save discount');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setEditingDiscount(null);
    setFormData({});
    setIsNewDiscount(false);
    setIsCopyDiscount(false);
  };

  const formatDiscountValue = (discount: AllDiscountTypes) => {
    if (discount.type === 'duration_free') {
      const hours = discount.freeDurationMinutes ? discount.freeDurationMinutes / 60 : 0;
      let display = `${hours} hour${hours !== 1 ? 's' : ''} free`;
      
      if (discount.additionalHourPricing === 'discounted' && discount.additionalHourDiscount) {
        display += `, then ${discount.additionalHourDiscount}% off`;
      } else if (discount.additionalHourPricing === 'fixed_rate' && discount.additionalHourRate) {
        display += `, then $${discount.additionalHourRate}/hr`;
      }
      
      return display;
    }
    
    return `${discount.amount}${discount.type === 'percentage' ? '%' : '$'}`;
  };

  const getDiscountName = (discount: AllDiscountTypes) => {
    if (discount.discountType === 'promo') {
      return (discount as PromoDiscount).code;
    }
    return (discount as UserDiscount).name;
  };

  const renderDiscountTypeSpecificFields = () => {
    if (!formData.discountType) return null;

    if (formData.discountType === 'promo') {
      return (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Promo Code"
              value={formData.code || ''}
              onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Internal Name (Optional)"
              value={formData.name || ''}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              helperText="Internal reference for complex promo codes"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Usage Limit"
              type="number"
              value={formData.usageLimit || ''}
              onChange={(e) => setFormData({...formData, usageLimit: e.target.value ? parseInt(e.target.value) : null})}
              helperText="Leave empty for unlimited uses"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Minimum Purchase Amount"
              type="number"
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              value={formData.minPurchaseAmount || ''}
              onChange={(e) => setFormData({...formData, minPurchaseAmount: e.target.value ? parseFloat(e.target.value) : null})}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>User Restriction</InputLabel>
              <Select
                value={formData.userRestriction || 'none'}
                onChange={(e) => setFormData({...formData, userRestriction: e.target.value})}
                label="User Restriction"
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="new">New Customers Only</MenuItem>
                <MenuItem value="returning">Returning Customers Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Time-based restrictions (optional) */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Time Restrictions (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Leave empty to allow promo code use at any time.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Days of Week</Typography>
            <FormGroup row>
              {daysOfWeek.map((day) => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      checked={formData.daysOfWeek?.includes(day) || false}
                      onChange={(e) => {
                        const currentDays = formData.daysOfWeek || [];
                        if (e.target.checked) {
                          setFormData({...formData, daysOfWeek: [...currentDays, day]});
                        } else {
                          setFormData({...formData, daysOfWeek: currentDays.filter((d: string) => d !== day)});
                        }
                      }}
                    />
                  }
                  label={day.charAt(0).toUpperCase() + day.slice(1)}
                />
              ))}
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Hours of Day (comma-separated, 0-23)"
              value={formData.hoursOfDay?.join(', ') || ''}
              onChange={(e) => {
                const hours = e.target.value.split(',').map(h => parseInt(h.trim())).filter(h => !isNaN(h) && h >= 0 && h <= 23);
                setFormData({...formData, hoursOfDay: hours});
              }}
              helperText="Example: 6, 7, 8 for 6am-8am, or leave empty for all hours"
            />
          </Grid>
        </>
      );
    }

    // Time-based discounts are now consolidated into promo codes

    if (formData.discountType === 'user') {
      return (
        <>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Discount Name"
              value={formData.name || ''}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                value={formData.userType || 'all'}
                onChange={(e) => setFormData({...formData, userType: e.target.value})}
                label="User Type"
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="new">New Customers</MenuItem>
                <MenuItem value="returning">Returning Customers</MenuItem>
                <MenuItem value="loyal">Loyal Customers</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Minimum Bookings (for loyal customers)"
              type="number"
              value={formData.minBookings || ''}
              onChange={(e) => setFormData({...formData, minBookings: e.target.value ? parseInt(e.target.value) : null})}
              disabled={formData.userType !== 'loyal'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Maximum Uses per Customer"
              type="number"
              value={formData.maxUses || ''}
              onChange={(e) => setFormData({...formData, maxUses: e.target.value ? parseInt(e.target.value) : null})}
              helperText="Leave empty for unlimited"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Total Usage Limit"
              type="number"
              value={formData.usageLimit || ''}
              onChange={(e) => setFormData({...formData, usageLimit: e.target.value ? parseInt(e.target.value) : null})}
              helperText="Maximum total uses across all customers (leave empty for unlimited)"
            />
          </Grid>
        </>
      );
    }

    return null;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Discounts Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddDiscount}
            disabled={tabValue === 0}
          >
            Add Discount
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Search and Filter Controls */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search discounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="All Discounts" />
            <Tab label="Promo Codes" />
            <Tab label="User-based" />
          </Tabs>
        </Paper>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name/Code</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDiscounts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((discount) => (
                    <TableRow key={discount.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {getDiscountName(discount)}
                          </Typography>
                          {(discount.discountType === 'promo' || discount.discountType === 'user') && (
                            <Typography variant="caption" color="text.secondary">
                              Used: {(discount as PromoDiscount | UserDiscount).usageCount || 0}
                              {(discount as PromoDiscount | UserDiscount).usageLimit && ` / ${(discount as PromoDiscount | UserDiscount).usageLimit}`}
                            </Typography>
                          )}
                          {discount.discountType === 'promo' && ((discount as PromoDiscount).daysOfWeek?.length || (discount as PromoDiscount).hoursOfDay?.length) && (
                            <Typography variant="caption" color="warning.main" display="block">
                              Time restrictions
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={discount.discountType} 
                          size="small"
                          color={
                            discount.discountType === 'promo' ? 'primary' : 'success'
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDiscountValue(discount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={discount.isActive ? 'Active' : 'Inactive'}
                          color={discount.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {format(discount.startDate, 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        {discount.endDate ? format(discount.endDate, 'MMM d, yyyy') : 'No end date'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleEditDiscount(discount)} size="small" title="Edit">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleCopyDiscount(discount)} size="small" color="info" title="Copy">
                          <CopyIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(discount)} size="small" color="error" title="Delete">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredDiscounts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}

        {/* Edit/Create Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {isNewDiscount 
              ? (isCopyDiscount ? 'Copy Discount' : 'Create New Discount')
              : 'Edit Discount'
            }
          </DialogTitle>
          <DialogContent>
            {isCopyDiscount && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Creating a copy of the selected discount. The promo code and usage count have been reset.
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Basic Fields */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={formData.type || 'percentage'}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    label="Discount Type"
                  >
                    <MenuItem value="percentage">Percentage Off</MenuItem>
                    <MenuItem value="fixed">Fixed Amount Off</MenuItem>
                    <MenuItem value="duration_free">Duration Free (e.g., "1 Hour Free")</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.type === 'duration_free' ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Free Duration"
                      type="number"
                      value={formData.freeDurationMinutes ? formData.freeDurationMinutes / 60 : ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        freeDurationMinutes: parseFloat(e.target.value) * 60 || 0,
                        amount: parseFloat(e.target.value) || 0 // Set amount for display purposes
                      })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">hours</InputAdornment>
                      }}
                      required
                      helperText="Number of hours that will be free"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Additional Hours Pricing</InputLabel>
                      <Select
                        value={formData.additionalHourPricing || 'regular'}
                        onChange={(e) => setFormData({...formData, additionalHourPricing: e.target.value})}
                        label="Additional Hours Pricing"
                      >
                        <MenuItem value="regular">Regular Rate</MenuItem>
                        <MenuItem value="discounted">Discounted Rate</MenuItem>
                        <MenuItem value="fixed_rate">Fixed Rate</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {formData.additionalHourPricing === 'discounted' && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Additional Hours Discount"
                        type="number"
                        value={formData.additionalHourDiscount || ''}
                        onChange={(e) => setFormData({...formData, additionalHourDiscount: parseFloat(e.target.value) || 0})}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">% off</InputAdornment>
                        }}
                        helperText="Percentage discount on hours after free period"
                      />
                    </Grid>
                  )}
                  {formData.additionalHourPricing === 'fixed_rate' && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Additional Hours Rate"
                        type="number"
                        value={formData.additionalHourRate || ''}
                        onChange={(e) => setFormData({...formData, additionalHourRate: parseFloat(e.target.value) || 0})}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          endAdornment: <InputAdornment position="end">/hour</InputAdornment>
                        }}
                        helperText="Fixed rate per hour after free period"
                      />
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Discount Amount"
                    type="number"
                    value={formData.amount || ''}
                    onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">
                        {formData.type === 'percentage' ? '%' : '$'}
                      </InputAdornment>
                    }}
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate || null}
                  onChange={(date) => {
                    // Ensure the date is set to start of day in local timezone
                    if (date && date instanceof Date) {
                      const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                      setFormData({...formData, startDate: localDate});
                    } else {
                      setFormData({...formData, startDate: date});
                    }
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate || null}
                  onChange={(date) => {
                    // Ensure the date is set to end of day in local timezone
                    if (date && date instanceof Date) {
                      const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                      setFormData({...formData, endDate: localDate});
                    } else {
                      setFormData({...formData, endDate: date});
                    }
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive || false}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={vesselTypes}
                  value={formData.applicableVesselTypes || []}
                  onChange={(event, value) => setFormData({...formData, applicableVesselTypes: value})}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Applicable Vessel Types"
                      helperText="Leave empty to apply to all vessel types"
                    />
                  )}
                />
              </Grid>

              {/* Type-specific fields */}
              {renderDiscountTypeSpecificFields()}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSaveDiscount} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this discount? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default DiscountsPage; 